import React, { useEffect, useState } from "react";
import {
    Container,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Avatar,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    Link as MuiLink
} from '@mui/material';
import { Link } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BusinessIcon from '@mui/icons-material/Business';
import Footer from '../core/Footer';
import { listLeadership } from '../group/apigroup';

const PublicAbout = () => {
    const [committeeMembers, setCommitteeMembers] = useState([]);
    const [leadershipLoading, setLeadershipLoading] = useState(true);
    const [leadershipError, setLeadershipError] = useState('');

    useEffect(() => {
        listLeadership().then(data => {
            if (!data || data.error) {
                setLeadershipError((data && data.error) || 'Could not load committee leadership.');
                setLeadershipLoading(false);
                return;
            }
            const members = [];
            (Array.isArray(data) ? data : []).forEach(group => {
                if (group.chair) {
                    members.push({
                        role: `${group.name} Committee Chair`,
                        name: `${group.chair.name || ''} ${group.chair.lastName || ''}`.trim(),
                        company: group.chair.companyName || '',
                        userId: group.chair._id
                    });
                }
                if (group.cochair) {
                    members.push({
                        role: `${group.name} Committee Co-Chair`,
                        name: `${group.cochair.name || ''} ${group.cochair.lastName || ''}`.trim(),
                        company: group.cochair.companyName || '',
                        userId: group.cochair._id
                    });
                }
            });
            setCommitteeMembers(members);
            setLeadershipLoading(false);
        });
    }, []);

    const leadershipData = [
        {
            category: "Executive Leadership",
            icon: <EmojiEventsIcon sx={{ fontSize: 32, color: '#FF6600' }} />,
            color: '#003E7E',
            members: [
                { role: "Consortium Chair", name: "Scott Dimpfl", company: "American Electric Power", userId: "6091653ca6e6f139835b6bb2" },
                { role: "Consortium Vice Chair", name: "Crystal Myers", company: "DUKE ENERGY", userId: "618026b414203669b666a031"}
            ]
        },
        {
            category: "Committee Chairs & Co-Chairs",
            icon: <GroupsIcon sx={{ fontSize: 32, color: '#FF6600' }} />,
            color: '#003E7E',
            dynamic: true,
            members: committeeMembers
        },
        {
            category: "Vendor Representatives (Non-Voting)",
            icon: <BusinessIcon sx={{ fontSize: 32, color: '#FF6600' }} />,
            color: '#666',
            members: [
                { role: "Autodesk Representative", name: "Jack Hopson", company: "Autodesk", userId: "60e881c418cba90a9f85dda4" },
                { role: "SBS Representative", name: "Kevin Whyte", company: "Spatial Business Systems", userId: "65de2394178bde148dd896c0" }
            ]
        },
        {
            category: "Advisors & Specialists (Non-Voting)",
            icon: <InfoIcon sx={{ fontSize: 32, color: '#FF6600' }} />,
            color: '#666',
            members: [
                { role: "Contractor Advisor", name: "Jeremy Morton", company: "", userId: "5e95ca30e588f001335dc00b" },
                { role: "Contractor Advisor", name: "Jeff Spence", company: "", userId: "5ea9ecda06aea5d11b3b0310" },
                { role: "Manufacturing Advisor", name: "Erica Huggins Vogel", company: "", userId: "5ea9ed1e37e4b1aa9f936261" },
                { role: "SDSIC Advisor", name: "Trevor Scullion", company: "Co-Founder", userId: "5eac1de28dda1e957267c9a7" },
                { role: "SDSIC Advisor", name: "Mark Nelson", company: "Special Advisor", userId: "5e61560e33b0177e5838012d" },
                { role: "SDSIC Advisor", name: "Audrey Marich", company: "Special Advisor", userId: "5ea9ed1e37e4b1aa9f936207" },
                { role: "Documentation Specialist", name: "Haley Collins", company: "Burns & McDonnell", userId: "60ba34373100124c7cd4f71f" },
                { role: "Newsletter Writer", name: "Morgan James", company: "POWER Engineers", userId: "607f3905a4e2d471a5a5dcbd" }
            ]
        }
    ];

    return (
        <>
            {/* Hero Section */}
            <Box sx={{ backgroundColor: '#003E7E', color: 'white', py: 6, mb: 4 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <InfoIcon sx={{ fontSize: 56 }} />
                        <Box>
                            <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                About SDSIC
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                Substation Design Solution Industry Consortium
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ mb: 6 }}>
                {/* Mission Statement */}
                <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#003E7E' }}>
                        Our Mission
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                        Substation Design Solution (SDS) Industry Consortium is made up of member utilities
                        and contract/design firms which have adopted or are investigating for adoption the
                        new technologies or workflows to improve design, engineering, field construction,
                        operation and maintenance of utility projects.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                        The Consortium will meet, as needed, to establish common workflows, common toolkit
                        functionality, shared libraries, common standards, technology needs and any other
                        topics that help drive our vendor's software development efforts according to an industry standard.
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                        The SDS Industry Consortium will drive standards to promote common library development from
                        internal resources, external partners, and/or equipment manufacturers.
                    </Typography>
                </Paper>

                {/* Leadership Section */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: '#003E7E' }}>
                        Current Leadership 2026
                    </Typography>

                    {leadershipData.map((section, idx) => (
                        <Paper
                            key={idx}
                            elevation={2}
                            sx={{
                                p: 3,
                                mb: 3,
                                borderRadius: 3,
                                backgroundColor: 'rgba(0, 62, 126, 0.02)'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                {section.icon}
                                <Typography variant="h5" sx={{ fontWeight: 600, color: section.color }}>
                                    {section.category}
                                </Typography>
                            </Box>

                            {section.dynamic && leadershipLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={32} />
                                </Box>
                            )}

                            {section.dynamic && !leadershipLoading && leadershipError && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    {leadershipError}
                                </Alert>
                            )}

                            {section.dynamic && !leadershipLoading && !leadershipError && section.members.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    No committee leadership on record.
                                </Typography>
                            )}

                            <Grid container spacing={2}>
                                {section.members.map((member, memberIdx) => (
                                    <Grid item xs={12} sm={6} md={4} key={memberIdx}>
                                        <Card
                                            elevation={1}
                                            sx={{
                                                height: '100%',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 8px 16px rgba(0, 62, 126, 0.15)',
                                                }
                                            }}
                                        >
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 48,
                                                            height: 48,
                                                            backgroundColor: '#003E7E',
                                                            fontSize: '1.2rem',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {member.name.split(' ').map(n => n[0]).join('')}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1 }}>
                                                        {member.userId ? (
                                                            <MuiLink
                                                                component={Link}
                                                                to={`/user/${member.userId}`}
                                                                underline="hover"
                                                                sx={{
                                                                    textDecoration: 'none',
                                                                    '&:hover': {
                                                                        textDecoration: 'underline'
                                                                    }
                                                                }}
                                                            >
                                                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#003E7E' }}>
                                                                    {member.name}
                                                                </Typography>
                                                            </MuiLink>
                                                        ) : (
                                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#003E7E' }}>
                                                                {member.name}
                                                            </Typography>
                                                        )}
                                                        {member.company && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {member.company}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Chip
                                                    label={member.role}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'rgba(255, 102, 0, 0.1)',
                                                        color: '#FF6600',
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem'
                                                    }}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    ))}
                </Box>
            </Container>

            <Footer />
        </>
    );
};

export default PublicAbout;